/**
 * App.js - Main Application Entry
 * 
 * Sets up routing and authentication context.
 * Protected routes require authentication via our JWT.
 * Supports role-based access control for Admin, Customer, and Vendor roles.
 */

import './index.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { TelemetryProvider } from './context/TelemetryContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import MapPage from './components/MapPage';
import AssetManager from './components/AssetManager';
import Repairs from './components/Repairs';
import Admin from './components/Admin';
import Settings from './components/Settings';
import VendorManagement from './components/VendorManagement';
import Login from './components/Login';
import Signup from './components/Signup';
import AuthCallback from './components/AuthCallback';
import VendorInviteAccept from './components/VendorInviteAccept';
import SoftwareEvaluationForm from './components/SoftwareEvaluationForm';
import { ROLES } from './config/permissions';

// Vehicle Tracking Module
import VehicleTrackingModule from './modules/vehicle-tracking/VehicleTrackingModule';

// Workflows Module
import WorkflowsModule from './modules/workflows/WorkflowsModule';

// Delivery & Fulfillment Module
import DeliveryModule from './modules/delivery/DeliveryModule';

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <TelemetryProvider>
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/vendors/accept" element={<VendorInviteAccept />} />
              <Route path="/evaluation" element={<SoftwareEvaluationForm />} />

          {/* Protected routes - wrapped in Layout */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard - accessible by all roles (content varies by role) */}
            <Route index element={<Dashboard />} />
            
            {/* Repairs - accessible by all roles */}
            <Route path="repairs" element={<Repairs />} />
            
            {/* Map - auth required, feature gating inside component */}
            <Route path="map" element={<MapPage />} />
            
            {/* Asset Manager - auth required, feature gating inside component */}
            <Route path="assets" element={<AssetManager />} />
            
            {/* Admin Panel - auth required, feature gating inside tabs */}
            <Route path="admin" element={<Admin />} />
            
            {/* Settings */}
            <Route path="settings" element={<Settings />} />
            
            {/* Vendor Management - feature gated internally */}
            <Route path="vendors" element={<VendorManagement />} />

            {/* Vehicle Tracking Module - feature gated internally */}
            <Route path="vehicles/*" element={<VehicleTrackingModule />} />

            {/* Workflows Module */}
            <Route path="workflows/*" element={<WorkflowsModule />} />

            {/* Delivery & Fulfillment Module */}
            <Route path="delivery/*" element={<DeliveryModule />} />
          </Route>
        </Routes>
          </BrowserRouter>
        </TelemetryProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
